import { Accordion, Button } from '@capsicum/common-web';
import { type NCOnboardingPageProps } from '@capsicum/features/nc-onboarding/shared';
import { useDeleteAccount } from '@capsicum/socios-core';
import { Shell } from '@capsicum/socios-core-web';
import { useEffect, useMemo } from 'react';
import styled from 'styled-components';

import { useNCWExistingUsersTranslations } from '../../hooks';
import { NavigationMenu } from '../../ui/navigation-menu';

type SupportScreenProps = Pick<NCOnboardingPageProps, 'onBack' | 'onDelete'>;

export const SupportScreen = ({ onBack, onDelete }: SupportScreenProps) => {
	const { supportLink } = useDeleteAccount();
	const $t = useNCWExistingUsersTranslations(supportLink);

	useEffect(() => {
		const body = document.querySelector('body');

		body?.classList.add('height_auto');

		return () => {
			body?.classList.remove('height_auto');
		};
	}, []);

	const accordionItems = useMemo(
		() => [
			{
				content: <S.SectionDescription>{$t('ncOnboard.support.notes.one.description')}</S.SectionDescription>,
				id: '1',
				isExpanded: true,
				title: <S.SectionTitle>{$t('ncOnboard.support.notes.one.title')}</S.SectionTitle>
			},
			{
				content: <S.SectionDescription>{$t('ncOnboard.support.notes.two.description')}</S.SectionDescription>,
				id: '2',
				isExpanded: false,
				title: <S.SectionTitle>{$t('ncOnboard.support.notes.two.title')}</S.SectionTitle>
			},
			{
				content: <S.SectionDescription>{$t('ncOnboard.support.notes.three.description')}</S.SectionDescription>,
				id: '3',
				isExpanded: false,
				title: <S.SectionTitle>{$t('ncOnboard.support.notes.three.title')}</S.SectionTitle>
			},
			{
				content: <S.SectionDescription>{$t('ncOnboard.support.notes.four.description')}</S.SectionDescription>,
				id: '4',
				isExpanded: false,
				title: <S.SectionTitle>{$t('ncOnboard.support.notes.four.title')}</S.SectionTitle>
			},
			{
				content: <S.SectionDescription>{$t('ncOnboard.support.notes.five.description')}</S.SectionDescription>,
				id: '5',
				isExpanded: false,
				title: <S.SectionTitle>{$t('ncOnboard.support.notes.five.title')}</S.SectionTitle>
			},
			{
				content: <S.SectionDescription>{$t('ncOnboard.support.notes.six.description')}</S.SectionDescription>,
				id: '6',
				isExpanded: false,
				title: <S.SectionTitle>{$t('ncOnboard.support.notes.six.title')}</S.SectionTitle>
			},
			{
				content: <S.SectionDescription>{$t('ncOnboard.support.notes.seven.description')}</S.SectionDescription>,
				id: '7',
				isExpanded: false,
				title: <S.SectionTitle>{$t('ncOnboard.support.notes.seven.title')}</S.SectionTitle>
			},
			{
				content: <S.SectionDescription>{$t('ncOnboard.support.notes.eight.description')}</S.SectionDescription>,
				id: '8',
				isExpanded: false,
				title: <S.SectionTitle>{$t('ncOnboard.support.notes.eight.title')}</S.SectionTitle>
			}
		],
		[$t]
	);

	return (
		<>
			<NavigationMenu title={$t('ncOnboard.support.title')} onClickBackButton={onBack} />
			<S.Wrapper>
				<Shell
					alignContent="flex-start"
					buttons={[
						<Button
							disabled={false}
							fullWidth
							key="confirm"
							testID="create-wallet-continue-button"
							text={$t('ncOnboard.support.actions.button')}
							onClick={onBack}
						/>,
						<S.TellMeMoreButton key="secondaryText" onClick={onDelete}>
							{$t('ncOnboard.support.actions.secondaryText')}
						</S.TellMeMoreButton>
					]}
					buttonsPosition="column"
					scrollOverContainer={false}
					showLogo={false}
					styles={{
						backgroundColor: 'transparent',
						height: 'auto'
					}}
				>
					<S.Content>
						<Accordion items={accordionItems} />
					</S.Content>
				</Shell>
			</S.Wrapper>
		</>
	);
};

namespace S {
	export const Wrapper = styled.div`
		display: flex;
		color: ${({ theme }) => theme.colors.text.main.enabled};
		background-color: ${({ theme }) => theme.colors.backgrounds.main.screen};
	`;

	export const Header = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.xxxsmall};
	`;

	export const Title = styled.h2`
		text-transform: uppercase;
		${({ theme }) => theme.fonts.headings.display3};
	`;

	export const Content = styled.div`
		display: flex;
		flex-direction: column;
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
		gap: ${({ theme }) => theme.spacings.xlarge};
		padding: ${({ theme }) => theme.spacings.small};
	`;

	export const Section = styled.div`
		margin-bottom: ${({ theme }) => theme.spacings.xsmall};
	`;

	export const SectionTitle = styled.h2`
		${({ theme }) => theme.fonts.text.textTopBar};
		margin-bottom: ${({ theme }) => theme.spacings.xxxsmall};
	`;

	export const SectionDescription = styled.p`
		${({ theme }) => theme.fonts.text.text3};
		margin-bottom: ${({ theme }) => theme.spacings.small};
		text-align: justify;
	`;

	export const TellMeMoreButton = styled.p`
		${({ theme }) => theme.fonts.text.text3};
		text-align: center;
		cursor: pointer;
	`;
}
